import { FC, useEffect, useMemo } from 'react';
import Icon from '@ant-design/icons';
import Flex from '@deprecated-ui/common/Flex';
import Text from '@deprecated-ui/common/Text';
import Button from '@deprecated-ui/common/Button';
import Modal from '@deprecated-ui/common/Modal';
import useShareTradeModal from 'features/sharetrade/stores/useShareTradeModal';
import ShareSvg from 'global/Icons/ShareSvg';
import Image from 'global/Image';
import { trackAutoBuyOrderReceivedView } from 'lib/analytics/autoOrder';
import { clevertapOrderReceived } from 'lib/analytics/securities';
import shallow from 'zustand/shallow';
import { ORDER_TYPE } from 'constants/trading';
import {
  BuyOrderType,
  OrderAction,
  SellOrderType,
} from 'global/Securities/types/transaction';
import { trackOrderShareSelected } from 'features/sharetrade/analytics/shareTradeShareTracker';
import color from '@deprecated-ui/_style/color';
import { useRouter } from 'next/router';
import useKeyPress from 'hooks/useKeyPress';
import { ShareTradeButton } from './styled';

interface PreviewModalProps {
  visible: boolean;
  onSubmit: () => void;
  onReturnToTransaction: () => void;
  onCloseModal: () => void;
  orderType: string;
  symbol?: string;
  returnTransactionLabel?: string;
}

const OrderPlacedModal: FC<PreviewModalProps> = ({
  visible,
  onSubmit,
  onReturnToTransaction,
  onCloseModal,
  orderType,
  symbol,
  returnTransactionLabel = 'Return to Orderbook',
}) => {
  const { pathname } = useRouter();
  const isChartbit = pathname.includes('/chartbit');
  const isAmend = useMemo(
    () =>
      orderType === ORDER_TYPE.AMEND_BUY || orderType === ORDER_TYPE.AMEND_SELL,
    [orderType],
  );

  const [shareOrderType, resetShareTradeModal] = useShareTradeModal(
    (s) => [s.orderType, s.resetShareTradeModal],
    shallow,
  );

  const isShowShareButton = useMemo(
    () =>
      !isAmend &&
      [BuyOrderType.LimitOrder, SellOrderType.LimitOrder].includes(
        shareOrderType,
      ),
    [isAmend, shareOrderType],
  );

  const [isShareTradeVisible, setIsShareTradeVisible, setSharedUsers] =
    useShareTradeModal(
      (s) => [s.isVisible, s.setIsVisible, s.setSharedUsers],
      shallow,
    );

  const isOrderModalVisible = useMemo(
    () => visible && !isShareTradeVisible,
    [visible, isShareTradeVisible],
  );

  const handleSubmit = () => {
    resetShareTradeModal();
    setSharedUsers({});
    if (isChartbit) onCloseModal();
    if (!isChartbit) onSubmit();
  };

  const handleShareTradeClick = () => {
    setIsShareTradeVisible(true);
    trackOrderShareSelected(orderType as OrderAction, symbol);
  };

  useEffect(() => {
    // don't track if the modal is visible and it is a bond transaction
    if (!visible || orderType === ORDER_TYPE.BOND) {
      return;
    }

    if (orderType) {
      clevertapOrderReceived({
        orderType,
      });

      trackAutoBuyOrderReceivedView();
    }
  }, [visible]);

  useKeyPress(['Enter'], () => {
    if (isOrderModalVisible) handleSubmit();
  });

  return (
    <Modal
      visible={isOrderModalVisible}
      width="420px"
      onCancel={onCloseModal}
      centered
      closeIcon={<Image src="/icon/modal-close.svg" />}
    >
      <Flex direction="column" align="center" padding="40px 32px 20px 32px">
        <Image
          src="/icon/preview-order-sent.svg"
          alt="order-sent"
          style={{ marginBottom: 16 }}
        />
        <Text size="18px" family="bold">
          Order Sent
        </Text>
        {isShowShareButton && (
          <Flex justify="center" margin="16px 0 0 0">
            <ShareTradeButton
              icon={<Icon component={ShareSvg} />}
              type="borderless"
              onClick={handleShareTradeClick}
            >
              <Text align="inherit" color="inherit" family="bold" size="14px">
                Share Trade
              </Text>
            </ShareTradeButton>
          </Flex>
        )}
      </Flex>
      <Flex padding="20px">
        {!isAmend && !isChartbit && (
          <Button
            onClick={onReturnToTransaction}
            type="default"
            radius="4px"
            block
            height="44px"
            margin="0 8px 0 0"
            style={{ borderColor: color.downgray }}
          >
            <Text
              align="inherit"
              color={color.primary}
              family="bold"
              size="14px"
            >
              {returnTransactionLabel}
            </Text>
          </Button>
        )}
        <Button
          onClick={handleSubmit}
          type="primary"
          radius="4px"
          block
          height="44px"
          margin="0 0 0 8px"
        >
          <Text align="inherit" color="inherit" family="bold" size="14px">
            Done
          </Text>
        </Button>
      </Flex>
    </Modal>
  );
};

export default OrderPlacedModal;
